import { Banner } from 'components/Banner/Banner'
import { BUTTON_TYPES } from 'components/Button'
import { useMyOrganizationCommunityPopup } from 'features/MyCommunity/MyOrganizationCommunity/Providers/MyOrganizationCommunityPopupProvider'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { TBusinessAccountDetails, TGetResolutionLink } from '../types'
import { GenerateResolutionLinkPopup } from '../GenerateResolutionLinkPopup'
import { ResolutionLinkGeneratedPopup } from '../ResolutionLinkGeneratedPopup'
import { getResolutionLinkRequest } from '../api'
import {
  MOOV_BUSINESS_ACCOUNT_STATUS,
  RESOLUTION_LINK_ERROR_TEXT,
  RESOLUTION_LINK_VISIBLE_ERRORS_COUNT
} from '../constants'
import { TYPOGRAPHY_WEIGHT, Typography } from 'components/Typography'
import { ReactComponent as TimerIcon } from 'assets/svg/Time24.svg'
import moment from 'moment'
import '../styles.scss'

export const ResolutionLinkWarningBlockContainer = ({
  businessAccountDetails,
  successCallback
}: {
  businessAccountDetails?: TBusinessAccountDetails
  successCallback: () => void
}) => {
  const { generateResolutionLinkPopup } = useMyOrganizationCommunityPopup()

  const showResolutionLinkBanner = useMemo(() => {
    if (!businessAccountDetails) return false
    return businessAccountDetails.capabilities.some((capability) =>
      capability.requirements?.errors.some(
        (error) => error.error_code !== 'document-failed-risk'
      )
    )
  }, [businessAccountDetails])

  const showResolutionLinkSubmittedBanner = useMemo(() => {
    if (!businessAccountDetails) return false
    return (
      businessAccountDetails.verification_status ===
      MOOV_BUSINESS_ACCOUNT_STATUS.IN_PROGRESS
    )
  }, [businessAccountDetails])

  const [linkGenerated, setLinkGenerated] = useState<TGetResolutionLink | null>(
    null
  )
  const [showAllErrors, setShowAllErrors] = useState(false)

  const resolutionLinkErrors = useMemo(() => {
    if (!businessAccountDetails?.capabilities) return []

    return businessAccountDetails.capabilities.flatMap(
      (capability) =>
        capability.requirements?.errors
          .map((error) => error.error_code)
          .filter((error) => error !== 'document-failed-risk') ?? []
    )
  }, [businessAccountDetails?.capabilities])

  const showExpandCollapse =
    resolutionLinkErrors.length > RESOLUTION_LINK_VISIBLE_ERRORS_COUNT

  const visibleResolutionLinkErrors = useMemo(() => {
    if (showAllErrors || !showExpandCollapse) {
      return resolutionLinkErrors
    }

    return resolutionLinkErrors.slice(0, RESOLUTION_LINK_VISIBLE_ERRORS_COUNT)
  }, [resolutionLinkErrors, showAllErrors, showExpandCollapse])

  useEffect(() => {
    setShowAllErrors(false)
  }, [businessAccountDetails?.uuid, resolutionLinkErrors.length])

  useEffect(() => {
    checkIfLinkGenerated()
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessAccountDetails?.uuid, showResolutionLinkBanner])

  const checkIfLinkGenerated = useCallback(async () => {
    if (
      !businessAccountDetails?.uuid ||
      !showResolutionLinkBanner ||
      !showResolutionLinkSubmittedBanner
    )
      return
    try {
      const response = await getResolutionLinkRequest(
        businessAccountDetails?.uuid
      )
      setLinkGenerated(response.data)
    } catch (error) {
      console.error(error)
    }
  }, [
    businessAccountDetails?.uuid,
    showResolutionLinkBanner,
    showResolutionLinkSubmittedBanner
  ])

  const daysLeft = useMemo(() => {
    return moment(linkGenerated?.expires_on).diff(moment(), 'day')
  }, [linkGenerated?.expires_on])

  const onGenerateResolutionLinkPopupSuccess = useCallback(() => {
    successCallback()
    // update resolution link to show timer & open link button
    checkIfLinkGenerated()
  }, [checkIfLinkGenerated, successCallback])

  if (
    showResolutionLinkSubmittedBanner &&
    linkGenerated &&
    linkGenerated.is_submitted_link_exists
  ) {
    return (
      <Banner.Info text="Your corrections have been submitted for review. Please await the final verification results. The Bank accounts tab will automatically enable once your setup is approved." />
    )
  }

  return (
    <>
      {showResolutionLinkBanner && (
        <Banner.Warning
          text={''}
          className="resolution-link-warning-block__banner"
          children={
            <div className="column gap-8 resolution-link-warning-block">
              <Typography.Body2 weight={TYPOGRAPHY_WEIGHT.SEMI_BOLD}>
                Action required, issues detected with your submission.
              </Typography.Body2>
              <ul className="resolution-link-warning-block__errors">
                {visibleResolutionLinkErrors.map((error, index) => (
                  <li key={`${error}-${index}`}>
                    <Typography.Body2>
                      {RESOLUTION_LINK_ERROR_TEXT[error] ?? error}
                    </Typography.Body2>
                  </li>
                ))}
              </ul>
              {showExpandCollapse && (
                <Typography.Body2
                  className="resolution-link-warning-block__toggle"
                  onClick={() => setShowAllErrors((prev) => !prev)}
                >
                  {showAllErrors
                    ? 'Show less'
                    : `Show all ${resolutionLinkErrors.length} errors`}
                </Typography.Body2>
              )}
              <Typography.Body2>
                {linkGenerated?.url ? (
                  <span>
                    Use the <span className="semibold">Resolution link</span> to
                    correct the affected information.
                  </span>
                ) : (
                  <span>
                    To resolve these issues, generate a personalized 
                    <span className="semibold">Resolution link</span> to correct
                    the affected information.
                  </span>
                )}
              </Typography.Body2>
            </div>
          }
          button={
            linkGenerated?.url
              ? [
                  {
                    buttonText: 'Open resolution link',
                    buttonType: BUTTON_TYPES.PRIMARY,
                    onButtonClick: () => {
                      window.open(linkGenerated?.url, '_blank')
                    },
                    underlineInfo: (
                      <div className="underline-timer row gap-4">
                        <TimerIcon />
                        <Typography.Caption>
                          Link expires in {daysLeft}{' '}
                          {daysLeft > 1 ? 'days' : 'day'}
                        </Typography.Caption>
                      </div>
                    )
                  }
                ]
              : [
                  {
                    onButtonClick: () =>
                      generateResolutionLinkPopup.actions.open({
                        uuid: businessAccountDetails?.uuid
                      }),
                    buttonText: 'Generate resolution link',
                    buttonType: BUTTON_TYPES.PRIMARY
                  }
                ]
          }
        />
      )}
      <GenerateResolutionLinkPopup
        successCallback={onGenerateResolutionLinkPopupSuccess}
      />
      <ResolutionLinkGeneratedPopup />
    </>
  )
}
